import { avatarInitial, avatarTint } from '@src/utils/chatAvatar';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

/**
 * 프로필 이미지 공통 컴포넌트.
 *
 * 프로필 URL은 출처가 제각각이라(구 버킷/신 버킷/카카오 CDN/NULL) 깨지기 쉽다.
 * 여기서 세 가지를 한 번에 처리한다.
 *  - URL이 없으면 이름 이니셜 아바타로 대체
 *  - 로딩에 실패해도(403·404·삭제) 이니셜 아바타로 대체
 *  - http:// 로 저장된 이미지를 https:// 로 올려 혼합 콘텐츠 차단을 피한다
 *
 * next/image의 도메인 허용목록에 걸리지 않도록 일반 img를 쓴다
 * (프로필은 크기가 작아 최적화 이득보다 안정성이 중요하다).
 */
type Props = {
  url?: string | null;
  name?: string;
  size?: number;
  className?: string;
  alt?: string;
  style?: React.CSSProperties;
};

/** 저장된 http:// 이미지를 https:// 로 승격 */
const toHttps = (url?: string | null) =>
  url ? url.replace(/^http:\/\//i, 'https://') : '';

const ProfileAvatar = ({
  url,
  name,
  size = 40,
  className,
  alt = '프로필',
  style,
}: Props) => {
  const [failed, setFailed] = useState(false);
  const src = toHttps(url);

  // url이 바뀌면 실패 상태를 초기화한다(목록 재사용 시 이전 실패가 남지 않도록).
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    const tint = avatarTint(name || 'u');
    return (
      <Initial
        className={className}
        style={style}
        size={size}
        tint={tint.tint}
        ink={tint.ink}
        aria-label={alt}
      >
        {avatarInitial(name)}
      </Initial>
    );
  }

  return (
    <Img
      className={className}
      style={style}
      size={size}
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
    />
  );
};

const Img = styled.img<{ size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  min-width: ${({ size }) => size}px;
  border-radius: 50%;
  object-fit: cover;
  background: #f4f4f4;
`;
const Initial = styled.div<{ size: number; tint: string; ink: string }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  min-width: ${({ size }) => size}px;
  border-radius: 50%;
  background: ${({ tint }) => tint};
  color: ${({ ink }) => ink};
  font-size: ${({ size }) => Math.round(size * 0.38)}px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
`;

export default ProfileAvatar;
